import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { EyeIcon, EyeSlashIcon, ShieldCheckIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import { isInternalDomain, isGoogleProviderEnabled, getApprovedWorkspaceDomains } from '../utils/authUtils';
import { mfaService } from '../services/mfaService';
import { showGoogleOneTap } from '../services/googleOneTapService';
import { auth } from '../config/firebase';
import { MultiFactorError } from 'firebase/auth';
import toast from 'react-hot-toast';

const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});

type LoginFormData = z.infer<typeof loginSchema>;

const MFA_CODE_LENGTH = 6;

const Login: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [isGoogleSignInLoading, setIsGoogleSignInLoading] = useState(false);
  const [hasAttemptedAutoSignIn, setHasAttemptedAutoSignIn] = useState(false);
  const [requiresMfa, setRequiresMfa] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaError, setMfaError] = useState<MultiFactorError | null>(null);
  const autoSignInTriggeredRef = useRef(false);
  const oneTapShownRef = useRef(false);
  const { login, loginWithGoogle: authLoginWithGoogle, completeMfaLogin, isLoading, isAuthenticated, user, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = (location.state as any)?.from?.pathname || '/dashboard';

  const searchParams = new URLSearchParams(location.search);
  const workspaceAuth = searchParams.get('workspace_auth') === 'true' || searchParams.get('workspace_login') === 'true';
  const googleEnabled = isGoogleProviderEnabled();
  const approvedDomains = getApprovedWorkspaceDomains();
  const isInternal = email && isInternalDomain(email);
  const shouldShowGoogleButton = googleEnabled && (isInternal || workspaceAuth);
  const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;
  const shouldShowOneTap = googleEnabled && approvedDomains.length > 0 && !!googleClientId;

  const handleGoogleSignIn = useCallback(async () => {
    try {
      setIsGoogleSignInLoading(true);
      await authLoginWithGoogle();

      // After successful Google sign-in, AuthContext state is updated immediately
      // Navigate to dashboard - the redirect useEffect will also catch this
      navigate(from, { replace: true });
    } catch (error: any) {
      console.error('Google sign-in - error:', error);
      if (!workspaceAuth) {
        setHasAttemptedAutoSignIn(false);
      }
    } finally {
      setIsGoogleSignInLoading(false);
    }
  }, [workspaceAuth, navigate, from, authLoginWithGoogle]);

  // Redirect if already authenticated - only check once when auth state stabilizes
  useEffect(() => {
    if (isLoading) {
      return; // Wait for auth to finish loading
    }

    if (isAuthenticated && user && token) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate, from, user, token]);

  // Handle workspace auth auto-signin - only trigger once
  useEffect(() => {
    // Skip if already authenticated or loading
    if (isLoading || isAuthenticated || user || token) {
      return;
    }

    // Skip if conditions not met
    if (!workspaceAuth || !googleEnabled || hasAttemptedAutoSignIn || autoSignInTriggeredRef.current) {
      return;
    }

    // Check Firebase user one more time
    const currentFirebaseUser = auth.currentUser;
    if (currentFirebaseUser) {
      return;
    }

    // Use a longer delay to ensure auth state has settled
    const timeoutId = setTimeout(() => {
      // Final check before triggering
      const finalCheckFirebaseUser = auth.currentUser;
      if (finalCheckFirebaseUser || isAuthenticated || user || token || isLoading) {
        return;
      }

      if (autoSignInTriggeredRef.current) {
        return; // Already triggered
      }

      autoSignInTriggeredRef.current = true;
      setHasAttemptedAutoSignIn(true);
      handleGoogleSignIn();
    }, 500); // Increased delay to allow auth state to settle

    return () => {
      clearTimeout(timeoutId);
    };
  }, [workspaceAuth, googleEnabled, isAuthenticated, isLoading, hasAttemptedAutoSignIn, handleGoogleSignIn, user, token]);

  // Google One Tap: show prompt when user has a Google session from an approved workspace domain
  useEffect(() => {
    if (!shouldShowOneTap || isAuthenticated || isLoading || user || token || oneTapShownRef.current) {
      return;
    }
    const timeoutId = setTimeout(() => {
      if (auth.currentUser || isAuthenticated || user || token) return;
      oneTapShownRef.current = true;
      showGoogleOneTap({
        clientId: googleClientId!,
        onSuccess: () => navigate(from, { replace: true }),
        onFailure: (message) => {
          toast.error(message);
          oneTapShownRef.current = false; // allow retry
        },
      });
    }, 800);
    return () => clearTimeout(timeoutId);
  }, [shouldShowOneTap, isAuthenticated, isLoading, user, token, googleClientId, navigate, from]);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const watchedEmail = watch('email');

  useEffect(() => {
    if (watchedEmail) {
      setEmail(watchedEmail);
    }
  }, [watchedEmail]);

  const onSubmit = async (data: LoginFormData) => {
    try {
      const result = await login(data);

      // Check if MFA is required
      if ('requiresMfa' in result && result.requiresMfa) {
        setRequiresMfa(true);
        const mfaResult = result as unknown as { requiresMfa: true; error: MultiFactorError };
        setMfaError(mfaResult.error);
        return;
      }

      // After successful login, navigate to dashboard
      // The AuthContext login function already sets user and token, so we can navigate immediately
      navigate(from, { replace: true });
    } catch (error: any) {
      console.error('Login onSubmit - error:', error);
      toast.error(error.message || 'Login failed');
    }
  };

  const handleMfaVerification = async () => {
    if (!mfaCode || mfaCode.length !== MFA_CODE_LENGTH || !mfaError) {
      toast.error(`Please enter a valid ${MFA_CODE_LENGTH}-digit code`);
      return;
    }

    try {
      const mfaToken = await mfaService.handleSignInMfa(mfaError, mfaCode);
      await completeMfaLogin(mfaToken);

      // After successful MFA login, AuthContext state is updated immediately
      // Navigate to dashboard - the redirect useEffect will also catch this
      navigate(from, { replace: true });
    } catch (error: any) {
      console.error('Login - MFA verification failed:', error);
      toast.error(error.message || 'Invalid MFA code. Please try again.');
      setMfaCode('');
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 flex flex-col">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-40 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-r from-primary-50/20 to-secondary-50/20"></div>
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, rgba(37, 56, 188, 0.05) 0%, transparent 50%), radial-gradient(circle at 75% 75%, rgba(101, 118, 230, 0.05) 0%, transparent 50%)`
        }}></div>
      </div>

      <div className="relative flex-1 flex h-screen overflow-hidden">
        {/* Left Panel - Branding & Features */}
        <div className="hidden lg:flex lg:w-1/2 xl:w-3/5 bg-gradient-to-br from-primary-700 via-primary-800 to-primary-900 relative overflow-hidden">
          {/* Decorative Elements */}
          <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
            <div className="absolute top-20 left-20 w-32 h-32 bg-white/10 rounded-full blur-xl"></div>
            <div className="absolute bottom-20 right-20 w-48 h-48 bg-secondary-500/20 rounded-full blur-2xl"></div>
            <div className="absolute top-1/2 left-1/3 w-24 h-24 bg-primary-300/15 rounded-full blur-lg"></div>
          </div>

          <div className="relative z-10 w-full h-full overflow-y-auto">
            <div className="min-h-full flex flex-col justify-center px-12 xl:px-16 text-white py-12">
              <div className="max-w-md">
                {/* Logo Placeholder */}
                <div className="flex items-center mb-10">
                  <img
                    src="/assets/logo-white.svg"
                    alt="CAPRI Compass Logo"
                    className="h-16 xl:h-24 w-auto"
                  />
                </div>

                <h2 className="text-4xl xl:text-5xl font-bold mb-6 leading-tight">
                  Confirm You're Headed in the Right Direction
                </h2>
                <p className="text-xl text-primary-100 mb-8 leading-relaxed">
                  Overarching maturity assessment and strategic guidance to help your brands optimize operations and deliver exceptional results
                </p>

                {/* Feature List */}
                <div className="space-y-4">
                  <div className="flex items-center">
                    <ShieldCheckIcon className="w-6 h-6 text-primary-200 mr-3 flex-shrink-0" />
                    <span className="text-primary-100">Multi-dimensional assessment framework</span>
                  </div>
                  <div className="flex items-center">
                    <ChartBarIcon className="w-6 h-6 text-primary-200 mr-3 flex-shrink-0" />
                    <span className="text-primary-100">AI-powered insights and recommendations</span>
                  </div>
                  <div className="flex items-center">
                    <ShieldCheckIcon className="w-6 h-6 text-primary-200 mr-3 flex-shrink-0" />
                    <span className="text-primary-100">Real-time progress tracking</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Panel - Login Form */}
        <div className="w-full lg:w-1/2 xl:w-2/5 h-full overflow-y-auto bg-neutral-50/50 backdrop-blur-sm relative z-10">
          <div className="lg:hidden w-full bg-gradient-to-r from-primary-700 via-primary-800 to-primary-900 px-6 py-5">
            <div className="flex items-center justify-center">
              <img
                src="/assets/logo-white.svg"
                alt="CAPRI Compass Logo"
                className="h-14 w-auto"
              />
            </div>
          </div>

          <div className="min-h-full flex flex-col justify-center px-4 sm:px-6 lg:px-8 xl:px-12 py-8 lg:py-12">
            <div className="w-full max-w-md mx-auto space-y-8">
              <div className="text-center">
                <h2 className="text-3xl font-bold text-neutral-900 mb-2">
                  Welcome Back
                </h2>
                <p className="text-neutral-600">
                  Sign in to your CompassAI Dashboard
                </p>
              </div>

              {!requiresMfa ? (
                <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-neutral-700 mb-2">
                        Email Address
                      </label>
                      <input
                        {...register('email')}
                        type="email"
                        autoComplete="email"
                        className="w-full px-4 py-3 border border-neutral-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200 text-neutral-900 placeholder-neutral-500"
                        placeholder="Enter your email"
                      />
                      {errors.email && (
                        <p className="mt-2 text-sm text-error-600">{errors.email.message}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="password" className="block text-sm font-medium text-neutral-700 mb-2">
                        Password
                      </label>
                      <div className="relative">
                        <input
                          {...register('password')}
                          type={showPassword ? 'text' : 'password'}
                          autoComplete="current-password"
                          className="w-full px-4 py-3 pr-12 border border-neutral-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200 text-neutral-900 placeholder-neutral-500"
                          placeholder="Enter your password"
                        />
                        <button
                          type="button"
                          className="absolute inset-y-0 right-0 pr-4 flex items-center"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? (
                            <EyeSlashIcon className="h-5 w-5 text-neutral-400 hover:text-neutral-600" />
                          ) : (
                            <EyeIcon className="h-5 w-5 text-neutral-400 hover:text-neutral-600" />
                          )}
                        </button>
                      </div>
                      {errors.password && (
                        <p className="mt-2 text-sm text-error-600">{errors.password.message}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="text-sm">
                      <Link
                        to="/forgot-password"
                        className="font-medium text-primary-600 hover:text-primary-500 transition-colors duration-200"
                      >
                        Forgot your password?
                      </Link>
                    </div>
                  </div>

                  {shouldShowGoogleButton && (
                    <div>
                      <button
                        type="button"
                        onClick={handleGoogleSignIn}
                        disabled={isGoogleSignInLoading || isLoading}
                        className="w-full flex justify-center items-center py-3 px-4 border-2 border-neutral-300 rounded-xl text-sm font-medium text-neutral-700 bg-white hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:shadow-md"
                      >
                        {isGoogleSignInLoading ? (
                          <>
                            <LoadingSpinner size="sm" className="text-neutral-600 mr-2" />
                            Signing in with Google...
                          </>
                        ) : (
                          <>
                            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                            </svg>
                            Sign in with Google
                          </>
                        )}
                      </button>
                      <div className="mt-3 text-center">
                        <span className="text-sm text-neutral-500">or</span>
                      </div>
                    </div>
                  )}

                  <div>
                    <button
                      type="submit"
                      disabled={isLoading || isGoogleSignInLoading}
                      className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-xl text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl"
                    >
                      {isLoading ? (
                        <>
                          <LoadingSpinner size="sm" className="text-white mr-2" />
                          Signing in...
                        </>
                      ) : (
                        'Sign in to Dashboard'
                      )}
                    </button>
                  </div>
                </form>
              ) : (
                <div className="mt-8 space-y-6">
                  <div className="bg-primary-50 border border-primary-200 rounded-xl p-4 mb-6">
                    <div className="flex items-start">
                      <ShieldCheckIcon className="h-5 w-5 text-primary-600 mt-0.5 mr-3 flex-shrink-0" />
                      <div>
                        <h3 className="text-sm font-medium text-primary-900 mb-1">
                          Multi-Factor Authentication Required
                        </h3>
                        <p className="text-sm text-primary-700">
                          Please enter the 6-digit code from your authenticator app to complete sign-in.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label htmlFor="mfaCode" className="block text-sm font-medium text-neutral-700 mb-2">
                        Verification Code
                      </label>
                      <input
                        id="mfaCode"
                        type="text"
                        value={mfaCode}
                        onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, MFA_CODE_LENGTH))}
                        placeholder="000000"
                        maxLength={MFA_CODE_LENGTH}
                        autoFocus
                        className="w-full px-4 py-3 border border-neutral-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200 text-neutral-900 placeholder-neutral-500 text-center text-2xl tracking-widest font-mono"
                      />
                    </div>

                    <div className="flex space-x-3">
                      <button
                        type="button"
                        onClick={() => {
                          setRequiresMfa(false);
                          setMfaCode('');
                          setMfaError(null);
                        }}
                        className="flex-1 px-4 py-3 border border-neutral-300 rounded-xl text-neutral-700 hover:bg-neutral-50 transition-colors duration-200 font-medium"
                      >
                        Back
                      </button>
                      <button
                        type="button"
                        onClick={handleMfaVerification}
                        disabled={isLoading || mfaCode.length !== MFA_CODE_LENGTH}
                        className="flex-1 px-4 py-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 font-medium"
                      >
                        {isLoading ? (
                          <span className="flex items-center justify-center">
                            <LoadingSpinner size="sm" className="mr-2" />
                            Verifying...
                          </span>
                        ) : (
                          'Verify & Sign In'
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {!requiresMfa && (
                <div className="text-center">
                  <span className="text-sm text-neutral-600">
                    Don't have an account?{' '}
                    <Link
                      to="/register"
                      className="font-medium text-primary-600 hover:text-primary-500 transition-colors duration-200"
                    >
                      Contact your administrator
                    </Link>
                  </span>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
